import React, { useEffect, useState } from 'react';

import AddIcon from '@material-ui/icons/Add';
import CloseIcon from '@material-ui/icons/Close';
import { isEqual } from 'lodash';

import { languageIcons } from 'Components-redesign/Form/fieldTypes';
import { useI18n } from 'Hooks';
import { getLangText } from 'utils/langText';

import { Heading } from '../../Typography/Heading';
import PriceInput from '../PriceInput';
import TextInput from '../TextInput';
import { AddRow, Labels, RemoveButton, Row, Wrapper } from './styles';

type Value = { id: string; value: string };

export interface Props {
  title: string;
  labels: string[];
  values: Value[];
  languages?: string[];
  handleChange: (values: Value[]) => void;
}

export const MultiInputs = ({ title, labels, values, languages, handleChange }: Props) => {
  const [allValues, setAllValues]: any = useState([]);
  const { t } = useI18n();

  useEffect(() => {
    const equal = isEqual(allValues, values);
    if (!equal && values !== undefined && allValues.length == 0) {
      setAllValues([...values]);
    }
  }, [values]);

  const changeValues = (index, type, val, lang?) => {
    const newArr = [...allValues];
    if (lang) {
      newArr[index][lang] = val;
      const languagesObjct = Object.keys(newArr[index])
        .filter((key) => languages?.includes(key))
        .reduce((obj, key) => {
          obj[key] = newArr[index][key];
          return obj;
        }, {});
      newArr[index][type] = getLangText(languagesObjct);
    } else {
      newArr[index][type] = val;
    }
    setAllValues(newArr);
  };

  useEffect(() => {
    if (allValues) {
      handleChange(allValues);
    }
  }, [allValues]);

  return (
    <Wrapper>
      <Heading level={4} bottom={16}>
        {title}
      </Heading>
      {allValues.length > 0 && (
        <Labels inputFields={languages?.length}>
          <Heading level={6} color="var(--colorBlueGray600)">
            {labels[0]}
          </Heading>
          <Heading level={6} color="var(--colorBlueGray600)">
            {labels[1]}
          </Heading>
        </Labels>
      )}
      {allValues?.map((val, i) => (
        <Row key={i}>
          {languages?.length ? (
            languages?.map((lang) => {
              const langVal = val ? val[lang] : '';

              return (
                <TextInput
                  key={lang}
                  name={val.id}
                  value={langVal ?? ''}
                  handleChange={(e) => changeValues(i, 'id', e.target.value, lang)}>
                  {languages.length > 1 && <img src={languageIcons[lang]} />}
                </TextInput>
              );
            })
          ) : (
            <TextInput
              name={val.id ?? i}
              value={val.id ?? ''}
              handleChange={(e) => changeValues(i, 'id', e.target.value)}
            />
          )}
          {labels[1] == 'Price' ? (
            <PriceInput
              name={val.id ?? i}
              value={val.value ?? ''}
              handleChange={(name, val) => changeValues(i, 'value', val)}
            />
          ) : (
            <TextInput
              name={val.id ?? i}
              value={val.value ?? ''}
              handleChange={(e) => changeValues(i, 'value', e.target.value)}
            />
          )}
          <RemoveButton
            onClick={() =>
              setAllValues((prev) => prev.filter((item, itemIndex) => itemIndex !== i))
            }>
            <CloseIcon htmlColor="var(--colorBlueGray400)" />
          </RemoveButton>
        </Row>
      ))}
      <AddRow onClick={() => setAllValues((prev) => [...prev, { id: '', value: '' }])}>
        <AddIcon htmlColor="var(--colorUIBlue)" />
        <Heading level={5} color="var(--colorUIBlue)" bottom={0}>
          {t('General.Add')}
        </Heading>
      </AddRow>
    </Wrapper>
  );
};
